import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState('error'); setMessage('Missing verification token.'); return; }
    (async () => {
      try {
        const r = await axios.post(`${API}/customers/auth/verify-email`, { token });
        setState('success');
        setMessage(r.data?.message || 'Email verified.');
        setTimeout(() => navigate('/account', { replace: true }), 2500);
      } catch (err) {
        setState('error');
        setMessage(err.response?.data?.detail || 'Verification failed.');
      }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-16" style={{ backgroundColor: '#f7f9f3' }} data-testid="verify-email-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
        <div className="px-8 py-6 text-white text-center" style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
          {state === 'verifying' ? <Loader2 className="w-10 h-10 mx-auto mb-2 animate-spin" /> : state === 'success' ? <CheckCircle2 className="w-10 h-10 mx-auto mb-2" /> : <AlertCircle className="w-10 h-10 mx-auto mb-2" />}
          <h1 className="text-2xl font-bold">{state === 'verifying' ? 'Confirming\u2026' : state === 'success' ? 'Email confirmed' : 'Verification failed'}</h1>
        </div>
        <div className="px-8 py-7 text-center space-y-4">
          <p className="text-sm text-slate-700" data-testid="verify-email-message">{message || 'Please wait while we confirm your email.'}</p>
          {state === 'error' ? (
            <Link to="/account" className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:underline" data-testid="verify-email-resend"><Mail className="w-4 h-4" /> Go to Account to resend</Link>
          ) : state === 'success' ? (
            <Link to="/account" className="inline-block px-5 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium">Go to my account</Link>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
}
